import type { QueryClient } from "@tanstack/react-query"

import { ConfigPollerQuery } from "../../../modules/config/configPollerApi"
import { UserWeekMenu } from "../../../modules/nonAdminUser/reports/home/weekMenuApi"
import { UserQuery } from "../../../modules/users/userApi"
import { NotificationQuery } from "../../layouts/components/NotificationTopBar/notificationApi"
import { SupportInstructionsQuery } from "../../layouts/components/supportInstructions/instructionApi"

/**
 * An order touches data on almost every screen (production, service center, accounting,
 * archives, calendars, wallets), so we refresh everything except the queries an order
 * can never affect. Deliberately a deny-list: a missing entry here costs one redundant
 * refetch, while a missing entry in an allow-list costs a silently stale table — which
 * is exactly the class of bug this replaces.
 */
const ORDER_INDEPENDENT_QUERY_NAMES: ReadonlySet<string> = new Set<string>([
    // Session config/identity — refetching re-fires the ConfigPoller effects that dispatch into Redux.
    ConfigPollerQuery.CLIENT_CONFIGURATION,
    UserQuery.USER_DETAILS,
    UserQuery.ADMIN_DETAILS,
    // Static support documents.
    SupportInstructionsQuery.INSTRUCTIONS_FOR_SUPPLIER,
    SupportInstructionsQuery.INSTRUCTIONS_FOR_COMPANY,
    SupportInstructionsQuery.INSTRUCTIONS_FOR_MERCHANT,
    // Self-polling counters (60s interval), unrelated to orders.
    NotificationQuery.NOTIFICATION_COUNT,
    "news-count",
])

// Free/busy keeps disabled observers, so a plain invalidate never refetches it.
const FORCE_REFETCH_QUERY_NAMES: ReadonlySet<string> = new Set<string>([
    UserWeekMenu.USER_MEETING_ORDER_FREE_BUSY_SCHEDULE,
])

function queryNameOf(queryKey: ReadonlyArray<unknown>): string | undefined {
    const [queryName] = queryKey
    return typeof queryName === "string" ? queryName : undefined
}

/**
 * Refreshes every screen that could show data the just-closed order dialog changed.
 *
 * Query keys are always `[queryName, dependencies]` (see `queryKeyFor`), and the default
 * `refetchType: "active"` means mounted screens refetch right away while everything else
 * is marked stale and refetches on its next mount. New screens therefore get correct
 * behaviour without being registered anywhere.
 */
export function invalidateOrderDataOnDialogClose(queryClient: QueryClient): void {
    queryClient.invalidateQueries({
        predicate: (query) => {
            const queryName = queryNameOf(query.queryKey)
            return queryName !== undefined && !ORDER_INDEPENDENT_QUERY_NAMES.has(queryName)
        },
    })

    FORCE_REFETCH_QUERY_NAMES.forEach((queryName) => {
        queryClient.invalidateQueries({ queryKey: [queryName], refetchType: "all" })
    })
}
